import { useState, useEffect } from 'react'
import { FiMessageSquare, FiTrash2, FiUser, FiClock, FiX, FiCopy, FiCheck, FiArchive, FiInbox, FiRefreshCw } from 'react-icons/fi'
import { RiRobot2Line } from 'react-icons/ri'
import { useAuth } from '../contexts/AuthContext'

interface Conversation {
  id: string
  client_id: string
  user_id: string
  username: string
  created_at: string
  updated_at: string
  last_message_at: string
  message_count: number
  last_user_message: string
  last_assistant_message: string
  session_type?: 'authenticated' | 'public'
  is_archived?: number
  archived_at?: string | null
}

interface Message {
  id: string
  conversation_id: string
  client_id: string
  role: 'user' | 'assistant'
  content: string
  created_at: string
  metadata: any
  feedback?: {
    id: string
    suggestedResponse: string
    status: string
    createdAt: string
    appliedToPrompt: boolean
  } | null
}

interface ConversationsPanelProps {
  isOpen: boolean
  onClose: () => void
  agentId?: string | null
}

export function ConversationsPanel({ isOpen, onClose, agentId }: ConversationsPanelProps) {
  const { authenticatedFetch } = useAuth()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copiedClientId, setCopiedClientId] = useState<string | null>(null)

  // Estados para arquivamento
  const [showArchived, setShowArchived] = useState(false)
  const [archivedConversations, setArchivedConversations] = useState<Conversation[]>([])
  const [isLoadingArchived, setIsLoadingArchived] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadConversations()
    }
  }, [isOpen, agentId])

  // Carregar arquivadas quando mudar para aba de arquivadas
  useEffect(() => {
    if (showArchived && archivedConversations.length === 0) {
      loadArchivedConversations()
    }
  }, [showArchived])

  const loadConversations = async () => {
    setIsLoading(true)
    setError(null)
    try {
      // Sempre buscar conversas webhook (visitantes WhatsApp)
      // Se tiver agentId, filtrar por agente
      const url = agentId
        ? `/api/webhook/conversations?agentId=${agentId}`
        : '/api/webhook/conversations'

      const response = await authenticatedFetch(url)

      if (!response.ok) {
        throw new Error('Erro ao carregar conversas')
      }

      const data = await response.json()
      setConversations(data.conversations || [])
    } catch (err: any) {
      setError(err.message)
      console.error('Erro ao carregar conversas:', err)
    } finally {
      setIsLoading(false)
    }
  }

  // Carregar conversas arquivadas
  const loadArchivedConversations = async () => {
    setIsLoadingArchived(true)
    try {
      const url = agentId
        ? `/api/webhook/conversations/archived?agentId=${agentId}`
        : '/api/webhook/conversations/archived'

      const response = await authenticatedFetch(url)

      if (!response.ok) {
        throw new Error('Erro ao carregar conversas arquivadas')
      }

      const data = await response.json()
      setArchivedConversations(data.conversations || [])
    } catch (err: any) {
      setError(err.message)
      console.error('Erro ao carregar conversas arquivadas:', err)
    } finally {
      setIsLoadingArchived(false)
    }
  }

  // Arquivar conversa
  const archiveConversation = async (conversationId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation()
    try {
      const response = await authenticatedFetch(`/api/webhook/conversations/${conversationId}/archive`, {
        method: 'POST'
      })
      if (!response.ok) throw new Error('Erro ao arquivar conversa')

      // Mover da lista ativa para arquivadas
      const conversation = conversations.find(c => c.id === conversationId)
      if (conversation) {
        setConversations(prev => prev.filter(c => c.id !== conversationId))
        setArchivedConversations(prev => [{ ...conversation, is_archived: 1, archived_at: new Date().toISOString() }, ...prev])
      }

      // Se era a conversa selecionada, limpar seleção
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null)
        setMessages([])
      }
    } catch (err: any) {
      setError(err.message)
    }
  }

  // Desarquivar conversa
  const unarchiveConversation = async (conversationId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation()
    try {
      const response = await authenticatedFetch(`/api/webhook/conversations/${conversationId}/unarchive`, {
        method: 'POST'
      })
      if (!response.ok) throw new Error('Erro ao desarquivar conversa')

      // Mover da lista arquivadas para ativa
      const conversation = archivedConversations.find(c => c.id === conversationId)
      if (conversation) {
        setArchivedConversations(prev => prev.filter(c => c.id !== conversationId))
        setConversations(prev => [{ ...conversation, is_archived: 0, archived_at: null }, ...prev])
      }

      // Se era a conversa selecionada, limpar seleção
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null)
        setMessages([])
      }
    } catch (err: any) {
      setError(err.message)
    }
  }

  const loadMessages = async (conversation: Conversation) => {
    setSelectedConversation(conversation)
    setIsLoading(true)
    setError(null)
    
    try {
      // Sempre buscar mensagens webhook para conversas de visitantes
      const response = await authenticatedFetch(
        `/api/webhook/conversations/${conversation.id}/messages`
      )
      
      if (!response.ok) {
        throw new Error('Erro ao carregar mensagens')
      }

      const data = await response.json()
      setMessages(data.messages || [])
    } catch (err: any) {
      setError(err.message)
      console.error('Erro ao carregar mensagens:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const deleteConversation = async (conversationId: string) => {
    if (!confirm('Tem certeza que deseja deletar esta conversa?')) return

    try {
      const response = await authenticatedFetch(
        `/api/webhook/conversations/${conversationId}`,
        { method: 'DELETE' }
      )
      
      if (!response.ok) {
        throw new Error('Erro ao deletar conversa')
      }

      // Atualizar lista
      setConversations(prev => prev.filter(c => c.id !== conversationId))
      
      // Se era a conversa selecionada, limpar seleção
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null)
        setMessages([])
      }
    } catch (err: any) {
      setError(err.message)
      console.error('Erro ao deletar conversa:', err)
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    
    if (days === 0) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    } else if (days === 1) {
      return 'Ontem'
    } else if (days < 7) {
      return `${days} dias atrás`
    } else {
      return date.toLocaleDateString('pt-BR')
    }
  }

  const copyClientId = (clientId: string) => {
    navigator.clipboard.writeText(clientId)
    setCopiedClientId(clientId)
    setTimeout(() => setCopiedClientId(null), 2000)
  }

  if (!isOpen) return null

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 glass-overlay z-50"
        onClick={onClose}
      />
      
      {/* Modal Container - com padding para criar espaço interno */}
      <div className="fixed inset-8 md:inset-12 lg:inset-20 z-50 flex items-center justify-center">
        <div className="glass-panel rounded-2xl w-full h-full flex flex-col relative p-6">
          
          {/* Erro */}
          {error && (
            <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive flex-shrink-0">
              {error}
            </div>
          )}

          {/* Conteúdo Principal - Grid com overflow controlado */}
          <div className="flex-1 min-h-0 relative">
            
            {/* Botão fechar integrado ao container - canto superior direito */}
            <button
              onClick={onClose}
              className="absolute -top-3 -right-3 z-30 p-2 bg-card border-2 border-border rounded-lg hover:bg-destructive hover:border-destructive transition-all shadow-lg group"
              title="Fechar"
            >
              <FiX className="w-5 h-5 text-muted-foreground group-hover:text-white" />
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
              
              {/* Lista de conversas */}
              <div className="lg:col-span-1 flex flex-col min-h-0">
                <div className="bg-muted/30 rounded-xl border-2 border-border flex flex-col h-full overflow-hidden shadow-sm">
                  {/* Abas: Ativas / Arquivadas */}
                  <div className="px-3 py-1.5 border-b border-border flex-shrink-0">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setShowArchived(false)}
                        className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                          !showArchived
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        <FiInbox className="w-3.5 h-3.5" />
                        <span>Ativas</span>
                        {conversations.length > 0 && (
                          <span className={`text-[10px] px-1 py-0.5 rounded-full ${!showArchived ? 'bg-primary-foreground/20' : 'bg-muted-foreground/20'}`}>
                            {conversations.length}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => setShowArchived(true)}
                        className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                          showArchived
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        <FiArchive className="w-3.5 h-3.5" />
                        <span>Arquivadas</span>
                        {archivedConversations.length > 0 && (
                          <span className={`text-[10px] px-1 py-0.5 rounded-full ${showArchived ? 'bg-primary-foreground/20' : 'bg-muted-foreground/20'}`}>
                            {archivedConversations.length}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto scrollbar-hidden p-3">
                    {/* Lista de Conversas Ativas */}
                    {!showArchived && (
                      <>
                        {isLoading && conversations.length === 0 ? (
                          <div className="p-12 text-center text-muted-foreground">
                            <FiRefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin opacity-50" />
                            Carregando...
                          </div>
                        ) : conversations.length === 0 ? (
                          <div className="p-12 text-center text-muted-foreground">
                            <FiMessageSquare className="w-10 h-10 mx-auto mb-3 opacity-50" />
                            <p className="text-sm">Nenhuma conversa ainda</p>
                          </div>
                        ) : (
                          conversations.map((conv) => (
                            <div
                              key={conv.id}
                              className={`group p-3 mb-2 rounded-lg border hover:border-primary/30 cursor-pointer transition-all ${
                                selectedConversation?.id === conv.id
                                  ? 'bg-primary/10 border-primary/50'
                                  : 'bg-card/50 border-transparent'
                              }`}
                              onClick={() => loadMessages(conv)}
                            >
                              {/* Linha 1: Username + Badge + Actions */}
                              <div className="flex items-center justify-between gap-2 mb-1.5">
                                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                  <FiUser className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                  <span className="text-sm font-semibold text-foreground truncate">
                                    {conv.username || 'Desconhecido'}
                                  </span>
                                  {conv.session_type === 'public' && (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded-md font-medium flex-shrink-0">
                                      Público
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <button
                                    onClick={(e) => archiveConversation(conv.id, e)}
                                    className="p-1 hover:bg-muted rounded transition-colors opacity-0 group-hover:opacity-100"
                                    title="Arquivar conversa"
                                  >
                                    <FiArchive className="w-3.5 h-3.5 text-muted-foreground" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      deleteConversation(conv.id)
                                    }}
                                    className="p-1 hover:bg-destructive/10 rounded transition-colors opacity-0 group-hover:opacity-100"
                                    title="Deletar conversa"
                                  >
                                    <FiTrash2 className="w-3.5 h-3.5 text-destructive" />
                                  </button>
                                </div>
                              </div>

                              {/* Linha 2: Última mensagem do usuário */}
                              {conv.last_user_message && (
                                <div className="flex items-center gap-1.5 mb-1 text-xs text-muted-foreground">
                                  <FiUser className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate flex-1">{conv.last_user_message}</span>
                                </div>
                              )}

                              {/* Linha 3: Última resposta do assistente */}
                              {conv.last_assistant_message && (
                                <div className="flex items-center gap-1.5 mb-1.5 text-xs text-primary">
                                  <FiMessageSquare className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate flex-1">{conv.last_assistant_message}</span>
                                </div>
                              )}

                              {/* Linha 4: Info compacta - ID + msgs + timestamp */}
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <button
                                  className="font-mono hover:text-primary truncate flex items-center gap-1 min-w-0"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    copyClientId(conv.client_id)
                                  }}
                                  title="Copiar ID"
                                >
                                  {copiedClientId === conv.client_id ? (
                                    <>
                                      <FiCheck className="w-3 h-3 text-green-500 flex-shrink-0" />
                                      <span className="text-green-500">OK</span>
                                    </>
                                  ) : (
                                    <>
                                      <FiCopy className="w-3 h-3 flex-shrink-0" />
                                      <span className="truncate">{conv.client_id}</span>
                                    </>
                                  )}
                                </button>
                                <span className="flex items-center gap-1 flex-shrink-0">
                                  <FiMessageSquare className="w-3 h-3" />
                                  {conv.message_count}
                                </span>
                                <span className="flex items-center gap-1 flex-shrink-0">
                                  <FiClock className="w-3 h-3" />
                                  {formatDate(conv.last_message_at)}
                                </span>
                              </div>
                            </div>
                          ))
                        )}
                      </>
                    )}

                    {/* Lista de Conversas Arquivadas */}
                    {showArchived && (
                      <>
                        {isLoadingArchived ? (
                          <div className="p-12 text-center text-muted-foreground">
                            <FiRefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin opacity-50" />
                            Carregando arquivadas...
                          </div>
                        ) : archivedConversations.length === 0 ? (
                          <div className="p-12 text-center text-muted-foreground">
                            <FiArchive className="w-10 h-10 mx-auto mb-3 opacity-50" />
                            <p className="text-sm">Nenhuma conversa arquivada</p>
                          </div>
                        ) : (
                          archivedConversations.map((conv) => (
                            <div
                              key={conv.id}
                              className={`group p-3 mb-2 rounded-lg border hover:border-primary/30 cursor-pointer transition-all ${
                                selectedConversation?.id === conv.id
                                  ? 'bg-primary/10 border-primary/50'
                                  : 'bg-card/50 border-transparent'
                              }`}
                              onClick={() => loadMessages(conv)}
                            >
                              {/* Linha 1: Username + Badge + Actions */}
                              <div className="flex items-center justify-between gap-2 mb-1.5">
                                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                  <FiUser className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                  <span className="text-sm font-semibold text-muted-foreground truncate">
                                    {conv.username || 'Desconhecido'}
                                  </span>
                                  <span className="text-[10px] px-1.5 py-0.5 bg-muted text-muted-foreground rounded-md font-medium flex-shrink-0">
                                    Arquivada
                                  </span>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <button
                                    onClick={(e) => unarchiveConversation(conv.id, e)}
                                    className="p-1 hover:bg-primary/20 rounded transition-colors opacity-0 group-hover:opacity-100"
                                    title="Desarquivar conversa"
                                  >
                                    <FiInbox className="w-3.5 h-3.5 text-primary" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      deleteConversation(conv.id)
                                    }}
                                    className="p-1 hover:bg-destructive/10 rounded transition-colors opacity-0 group-hover:opacity-100"
                                    title="Deletar conversa"
                                  >
                                    <FiTrash2 className="w-3.5 h-3.5 text-destructive" />
                                  </button>
                                </div>
                              </div>

                              {/* Linha 2: Última mensagem do usuário */}
                              {conv.last_user_message && (
                                <div className="flex items-center gap-1.5 mb-1 text-xs text-muted-foreground">
                                  <FiUser className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate flex-1">{conv.last_user_message}</span>
                                </div>
                              )}

                              {/* Linha 3: Última resposta do assistente */}
                              {conv.last_assistant_message && (
                                <div className="flex items-center gap-1.5 mb-1.5 text-xs text-muted-foreground">
                                  <FiMessageSquare className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate flex-1">{conv.last_assistant_message}</span>
                                </div>
                              )}

                              {/* Linha 4: Info compacta - ID + msgs + timestamp */}
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <button
                                  className="font-mono hover:text-primary truncate flex items-center gap-1 min-w-0"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    copyClientId(conv.client_id)
                                  }}
                                  title="Copiar ID"
                                >
                                  {copiedClientId === conv.client_id ? (
                                    <>
                                      <FiCheck className="w-3 h-3 text-green-500 flex-shrink-0" />
                                      <span className="text-green-500">OK</span>
                                    </>
                                  ) : (
                                    <>
                                      <FiCopy className="w-3 h-3 flex-shrink-0" />
                                      <span className="truncate">{conv.client_id}</span>
                                    </>
                                  )}
                                </button>
                                <span className="flex items-center gap-1 flex-shrink-0">
                                  <FiMessageSquare className="w-3 h-3" />
                                  {conv.message_count}
                                </span>
                                <span className="flex items-center gap-1 flex-shrink-0">
                                  <FiClock className="w-3 h-3" />
                                  {formatDate(conv.last_message_at)}
                                </span>
                              </div>
                            </div>
                          ))
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Mensagens da conversa selecionada */}
              <div className="lg:col-span-2 flex flex-col min-h-0">
                <div className="bg-muted/30 rounded-xl border-2 border-border h-full flex flex-col overflow-hidden shadow-sm">
                  {!selectedConversation ? (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-12">
                      <FiMessageSquare className="w-16 h-16 mb-4 opacity-50" />
                      <p className="text-lg font-medium">Selecione uma conversa</p>
                      <p className="text-sm">Escolha uma conversa à esquerda para ver as mensagens</p>
                    </div>
                  ) : (
                    <>
                      <div className="px-6 py-4 border-b-2 border-border flex-shrink-0 bg-muted/50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 pr-4">
                            <h3 className="font-bold text-foreground mb-2 text-lg">
                              {selectedConversation.username || 'Desconhecido'}
                            </h3>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                              <button
                                className="font-mono hover:text-primary flex items-center gap-1"
                                onClick={() => copyClientId(selectedConversation.client_id)}
                                title="Clique para copiar"
                              >
                                {copiedClientId === selectedConversation.client_id ? (
                                  <>
                                    <FiCheck className="w-3 h-3 text-green-500" />
                                    <span className="text-green-500">Client ID copiado!</span>
                                  </>
                                ) : (
                                  <>
                                    <FiCopy className="w-3 h-3" />
                                    Client ID: {selectedConversation.client_id}
                                  </>
                                )}
                              </button>
                              <span>{selectedConversation.message_count} mensagens</span>
                              <span>Criado em {formatDate(selectedConversation.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex-1 overflow-y-auto scrollbar-hidden p-6 space-y-4">
                        {messages.map((msg) => (
                          <div key={msg.id} className="space-y-2">
                            {/* Mensagem original */}
                            <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                              <div
                                className={`max-w-[80%] p-4 rounded-lg ${
                                  msg.role === 'user'
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted text-foreground'
                                }`}
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  {msg.role === 'user' ? (
                                    <FiUser className="w-4 h-4 flex-shrink-0" />
                                  ) : (
                                    <RiRobot2Line className="w-4 h-4 flex-shrink-0" />
                                  )}
                                  <span className="text-sm font-semibold">
                                    {msg.role === 'user' ? 'Você' : 'Sofia'}
                                  </span>
                                  <span className={`text-xs ${msg.role === 'user' ? 'opacity-70' : 'text-muted-foreground'}`}>
                                    {formatDate(msg.created_at)}
                                  </span>
                                </div>
                                <p className="whitespace-pre-wrap">{msg.content}</p>
                                
                                {msg.metadata && (
                                  <div className={`mt-2 pt-2 border-t ${msg.role === 'user' ? 'border-primary-foreground/20' : 'border-border'} text-xs ${msg.role === 'user' ? 'opacity-70' : 'text-muted-foreground'}`}>
                                    <div className="flex flex-wrap gap-2">
                                      {msg.metadata.searchMethod && (
                                        <span className={`px-2 py-1 rounded ${msg.role === 'user' ? 'bg-primary-foreground/20' : 'bg-accent'}`}>
                                          {msg.metadata.searchMethod}
                                        </span>
                                      )}
                                      {msg.metadata.chunksUsed && (
                                        <span className={`px-2 py-1 rounded ${msg.role === 'user' ? 'bg-primary-foreground/20' : 'bg-accent'}`}>
                                          {msg.metadata.chunksUsed} chunks
                                        </span>
                                      )}
                                      {msg.metadata.cost && (
                                        <span className={`px-2 py-1 rounded ${msg.role === 'user' ? 'bg-primary-foreground/20' : 'bg-accent'}`}>
                                          ${msg.metadata.cost.toFixed(6)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Bloco verde de feedback - renderizado logo após mensagem do assistente */}
                            {msg.feedback && msg.role === 'assistant' && (
                              <div className="flex justify-start">
                                <div className="max-w-[80%] bg-green-50 dark:bg-green-900/20 border-2 border-green-500 rounded-lg p-4">
                                  <div className="flex items-center gap-2 mb-2">
                                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                    </svg>
                                    <span className="text-sm font-bold text-green-700 dark:text-green-300">
                                      💡 Sugestão de Treinamento
                                    </span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                      msg.feedback.status === 'applied' 
                                        ? 'bg-blue-500 text-white' 
                                        : msg.feedback.status === 'approved'
                                        ? 'bg-green-500 text-white'
                                        : msg.feedback.status === 'rejected'
                                        ? 'bg-red-500 text-white'
                                        : 'bg-yellow-500 text-white'
                                    }`}>
                                      {msg.feedback.status === 'applied' ? 'Aplicada' : 
                                       msg.feedback.status === 'approved' ? 'Aprovada' :
                                       msg.feedback.status === 'rejected' ? 'Rejeitada' : 'Pendente'}
                                    </span>
                                  </div>
                                  <p className="text-sm text-green-800 dark:text-green-200 whitespace-pre-wrap mb-2">
                                    {msg.feedback.suggestedResponse}
                                  </p>
                                  <div className="text-xs text-green-600 dark:text-green-400">
                                    Sugerido em {formatDate(msg.feedback.createdAt)}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
